// TicketDetailPage.tsx — MJOS-043 alignment
//
// Renders a single ticket via BlockRenderer + architectureConfig.
// Locked architecture (myjarvis-dashboard skill, "Page architecture doctrine"):
// detail pages generate a block recipe from the row's structured columns,
// then render through the same code path as /dashboard-architecture and
// /skills/:slug.
//
// Read-only — no interactive editing. CRUD happens via psql or a dedicated
// edit surface (locked 2026-05-11).

import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, Loader2 } from "lucide-react";
import { useApi } from "@/lib/api";
import { architectureT as T } from "../blueprint/ArchitectureBlocks";

type ISC = { id: string; text: string; done: boolean };

type TicketDetail = {
  id: string;
  slug: string;
  project_id: string | null;
  project_name: string | null;
  goal_id: string | null;
  goal_title: string | null;
  parent_id: string | null;
  agent: string | null;
  title: string;
  status: "todo" | "in_progress" | "review" | "done" | "archived";
  tier: "E1" | "E2" | "E3" | "E4" | "E5" | null;
  current_step: string | null;
  kind: "bug" | "feature" | "chore" | "task" | null;
  severity: string | null;
  summary: string | null;
  problem: string | null;
  vision: string | null;
  out_of_scope: string | null;
  principles: string | null;
  constraints: string | null;
  goal: string | null;
  test_strategy: string | null;
  features: string | null;
  decisions: string | null;
  changelog: string | null;
  verification: string | null;
  iscs: ISC[];
  log: string | null;
  created_at: string;
  updated_at: string;
};

const STATUS_TONE: Record<TicketDetail["status"], { fg: string; bg: string; bd: string }> = {
  todo:        { fg: T.ink2, bg: T.bg2, bd: T.line },
  in_progress: { fg: T.amber, bg: T.amberSoft, bd: T.amber },
  review:      { fg: T.plum, bg: T.plumSoft, bd: T.plum },
  done:        { fg: T.green, bg: T.greenSoft, bd: T.green },
  archived:    { fg: T.ink3, bg: T.bg2, bd: T.line },
};

// The 12 ISA sections plus log — order matters, this is the contract.
const SECTIONS: { key: keyof TicketDetail; label: string; eyebrow: string }[] = [
  { key: "problem",       label: "Problem",          eyebrow: "01 — PROBLEM" },
  { key: "vision",        label: "Vision",           eyebrow: "02 — VISION" },
  { key: "out_of_scope",  label: "Out of scope",     eyebrow: "03 — OUT OF SCOPE" },
  { key: "principles",    label: "Principles",       eyebrow: "04 — PRINCIPLES" },
  { key: "constraints",   label: "Constraints",      eyebrow: "05 — CONSTRAINTS" },
  { key: "goal",          label: "Goal",             eyebrow: "06 — GOAL" },
  // 07 is criteria/ISCs — handled separately below
  { key: "test_strategy", label: "Test strategy",    eyebrow: "08 — TEST STRATEGY" },
  { key: "features",      label: "Features",         eyebrow: "09 — FEATURES" },
  { key: "decisions",     label: "Decisions",        eyebrow: "10 — DECISIONS" },
  { key: "changelog",     label: "Changelog",        eyebrow: "11 — CHANGELOG" },
  { key: "verification",  label: "Verification",     eyebrow: "12 — VERIFICATION" },
];

// ── THE LAYOUT (Erez, 2026-08-11) ───────────────────────────────────────────
// "The top part should summarise things so I can see in one view, and under it
// you can add more details, log and everything else."
//
// So the body is two layers, not one list of thirteen equal sections:
//
//   SUMMARY CARD — everything you need to decide whether to care. Title, the
//                  tags, one paragraph, and how far along the criteria are.
//                  It is sized to be read in about five seconds.
//   THE DETAIL   — the evidence, in labelled blocks, for whoever picks it up.
//
// Colour carries MEANING here rather than decoration: red is what is wrong,
// green is what good looks like or what is already done, plum is judgement,
// amber is a limit. That is what makes it scannable — you find the section you
// want by its colour before you have read its label.
// ── HOW EACH SECTION IS RENDERED (Erez, 2026-08-11) ─────────────────────────
// "I really don't like these coloured lines on the side, and it's too
// symmetric — make it more UI-specific for the sections."
//
// He is right, and the diagnosis is the useful part: a coloured left rule on
// every block is a UNIFORM answer to a question that is different for each
// section. When everything is a card with a stripe, the stripe stops carrying
// information and the page reads as one texture — which is exactly why it was
// hard to scan even after it got shorter.
//
// So each section gets the FORM its content deserves, and no stripes:
//
//   prose   the main read. No container at all, largest body type. Problem.
//   panel   a tinted block, no border. Goal — what good looks like.
//   quiet   secondary context, muted and smaller. Principles, constraints,
//           out of scope, decisions, vision, test strategy.
//   list    a compact rollup in muted type. What this ticket absorbs.
//   ticked  green, prefixed with a check. Work that is already done.
//   mono    monospace on grey. The log, and only the log.
//
// The variety IS the navigation: you can tell what kind of thing you are
// looking at before you read a word of it.
type SectionForm = "prose" | "panel" | "quiet" | "list" | "ticked" | "mono";

const SECTION_STYLE: Record<string, { form: SectionForm; label: string }> = {
  problem:       { form: "prose",  label: "The problem" },
  goal:          { form: "panel",  label: "What good looks like" },
  vision:        { form: "quiet",  label: "Vision" },
  out_of_scope:  { form: "quiet",  label: "Out of scope" },
  principles:    { form: "quiet",  label: "Principles" },
  constraints:   { form: "quiet",  label: "Constraints" },
  test_strategy: { form: "quiet",  label: "Test strategy" },
  decisions:     { form: "quiet",  label: "Decisions" },
  features:      { form: "list",   label: "What this rolls up" },
  changelog:     { form: "ticked", label: "Already done" },
  verification:  { form: "ticked", label: "Verification" },
};

/** The tiny subset of markdown these fields actually use. A full parser would
 *  be a dependency and a licence to write prose nobody scans; bold, italic and
 *  code are enough to emphasise a phrase inside a paragraph. */
/** ⭐ IMAGES, and they are the point of the field they mostly appear in.
 *
 * Erez, 1 September 2026: "let's start managing tickets with screenshots using
 * the tickets" — and then, looking at the first one: "I don't see any images in
 * the detail of the ticket."
 *
 * ⛔ A TICKET THAT SWALLOWS ITS OWN EVIDENCE IS WORSE THAN ONE WITH NONE,
 * because the writer believes it landed. I wrote four screen captures into
 * MJOS-228, the field stored them perfectly, and every render form here piped
 * the markdown through `inline()` — which escaped the text and dropped the
 * image. The proof existed and the reviewer could not see it.
 *
 * ⚠️ The URL is whitelisted to https, and the alt text is escaped by the two
 * replaces above running FIRST. This function's whole output goes through
 * dangerouslySetInnerHTML, so an unfiltered src here is an injection.
 */
const IMG = /!\[([^\]]*)\]\((https:\/\/[^)\s]+)\)/g;

function inline(md: string): string {
  return md
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(IMG, (_m, alt, src) =>
      `<figure style="margin:12px 0">` +
      `<img src="${src}" alt="${alt}" loading="lazy" decoding="async" ` +
      `style="display:block;width:100%;border-radius:8px;border:1px solid #E4E7EA" />` +
      (alt ? `<figcaption style="font-size:13px;color:#9CA3AF;margin-top:5px">${alt}</figcaption>` : "") +
      `</figure>`)
    .replace(/\*\*(.+?)\*\*/g, "<b>$1</b>")
    .replace(/`(.+?)`/g, '<code style="font-family:ui-monospace,Menlo,monospace;font-size:0.92em;background:#EEF3FA;padding:1px 4px;border-radius:3px">$1</code>')
    .replace(/(^|[^*])\*([^*]+?)\*/g, "$1<i>$2</i>")
    .replace(/\n/g, "<br/>");
}

function Pill({ text, fg, bg, bd }: { text: string; fg: string; bg: string; bd?: string }) {
  return (
    <span style={{
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
      fontSize: 11.5, fontWeight: 700, letterSpacing: "0.07em",
      textTransform: "uppercase", color: fg, background: bg,
      border: `1px solid ${bd ?? bg}`, padding: "3px 8px", borderRadius: 5,
      whiteSpace: "nowrap",
    }}>{text}</span>
  );
}

function Label({ text, color }: { text: string; color: string }) {
  return (
    <div style={{
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
      fontSize: 12, fontWeight: 700, letterSpacing: "0.11em",
      textTransform: "uppercase", color, marginBottom: 7,
    }}>{text}</div>
  );
}

function Section({ form, label, body }: { form: SectionForm; label: string; body: string }) {
  const html = { __html: inline(body) };

  if (form === "prose") {
    return (
      <div style={{ marginBottom: 22 }}>
        <Label text={label} color={T.ink3} />
        <div style={{ fontSize: 18, lineHeight: 1.62, color: T.ink }} dangerouslySetInnerHTML={html} />
      </div>
    );
  }

  if (form === "panel") {
    return (
      <div style={{
        background: T.greenSoft, borderRadius: 10,
        padding: "13px 15px", marginBottom: 18,
      }}>
        <Label text={label} color={T.green} />
        <div style={{ fontSize: 17, lineHeight: 1.58, color: T.ink2 }} dangerouslySetInnerHTML={html} />
      </div>
    );
  }

  if (form === "ticked") {
    return (
      <div style={{ marginBottom: 18, display: "flex", gap: 9, alignItems: "flex-start" }}>
        <span style={{ color: T.green, fontWeight: 700, fontSize: 15, marginTop: 2 }}>✓</span>
        <div>
          <Label text={label} color={T.green} />
          <div style={{ fontSize: 16.5, lineHeight: 1.58, color: T.ink2 }} dangerouslySetInnerHTML={html} />
        </div>
      </div>
    );
  }

  if (form === "mono") {
    return (
      <div style={{
        background: T.bg2, borderRadius: 8, padding: "11px 13px", marginBottom: 18,
      }}>
        <Label text={label} color={T.ink3} />
        <div style={{
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: 14, lineHeight: 1.6, color: T.ink3,
          whiteSpace: "pre-wrap", wordBreak: "break-word",
        }} dangerouslySetInnerHTML={html} />
      </div>
    );
  }

  // list + quiet — secondary context, deliberately smaller and greyer so the
  // eye skips it on the way down and finds it when it goes looking.
  return (
    <div style={{ marginBottom: 18 }}>
      <Label text={label} color={T.ink3} />
      <div style={{
        fontSize: form === "list" ? 16 : 16.4,
        lineHeight: 1.6, color: T.ink3,
      }} dangerouslySetInnerHTML={html} />
    </div>
  );
}

/** The ticket body, as a PAGE and as a PANE.
 *
 *  Erez, 2026-08-11: the Kanban and the detail are one app, not two screens.
 *  So this takes an optional slug instead of only reading the route — the
 *  board passes the selected card, /tickets/:slug still passes the URL.
 *
 *  @param slug     overrides the route param. The pane passes this.
 *  @param embedded drops the back-link and the page padding; the pane supplies
 *                  its own frame and its own way back (deselect).
 */
export function TicketDetailPage({
  slug: slugProp,
  embedded = false,
}: {
  slug?: string;
  embedded?: boolean;
} = {}) {
  // Hooks cannot be conditional, so the route param is always read and the
  // prop simply wins when present.
  const { slug: routeSlug = "" } = useParams<{ slug: string }>();
  const slug = slugProp ?? routeSlug;
  const api = useApi();
  const [ticket, setTicket] = useState<TicketDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!slug) return;
    setError(null);
    setTicket(null);
    (async () => {
      try {
        const res = await api(`/api/tickets/${slug}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as TicketDetail;
        if (!cancelled) setTicket(data);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load");
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);


  return (
    <div style={{
      fontFamily: "Inter, sans-serif",
      boxSizing: "border-box",
      padding: embedded ? 0 : "40px 48px 80px",
    }}>
      <div style={{ maxWidth: embedded ? "none" : 1000, margin: embedded ? 0 : "0 auto" }}>
        {!embedded && (
          <div style={{ marginBottom: 24 }}>
            <Link to="/tickets" style={{
              color: T.accent, fontSize: 15, textDecoration: "none",
              display: "inline-flex", alignItems: "center", gap: 6,
            }}>
              <ChevronLeft style={{ width: 14, height: 14 }} /> Tickets
            </Link>
          </div>
        )}

        {error ? (
          <div style={{
            padding: "16px 20px", color: T.red, background: T.redSoft,
            border: `1px solid ${T.red}`, borderRadius: 8, fontSize: 13,
          }}>
            Failed to load ticket: <code>{slug}</code> — {error}
          </div>
        ) : ticket === null ? (
          <div style={{ padding: 24, fontSize: 14, color: T.ink3,
            display: "inline-flex", alignItems: "center", gap: 8 }}>
            <Loader2 className="animate-spin" style={{ width: 14, height: 14 }} />
            Loading…
          </div>
        ) : (
          <>
            {/* ── LAYER 1 · THE SUMMARY CARD ─────────────────────────── */}
            <div style={{
              background: T.bg2, border: `1px solid ${T.line}`,
              borderRadius: 12, padding: "16px 18px", marginBottom: 16,
            }}>
              <div style={{
                display: "flex", alignItems: "center", gap: 8,
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                fontSize: 12.5, fontWeight: 700, letterSpacing: "0.08em",
                color: T.skyDark, marginBottom: 10,
              }}>
                <span>{ticket.slug}</span>
                <span style={{ color: T.ink3, fontWeight: 500 }}>
                  {[ticket.project_name, ticket.agent && `@${ticket.agent}`]
                    .filter(Boolean).join(" · ")}
                </span>
              </div>

              <h1 style={{
                fontSize: 23, fontWeight: 700, color: T.ink,
                margin: "0 0 12px", letterSpacing: "-0.015em", lineHeight: 1.25,
              }}>
                {ticket.title}
              </h1>

              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
                <Pill
                  text={ticket.status.replace("_", " ")}
                  fg={STATUS_TONE[ticket.status].fg}
                  bg={STATUS_TONE[ticket.status].bg}
                  bd={STATUS_TONE[ticket.status].bd}
                />
                {ticket.kind && ticket.kind !== "task" && (
                  <Pill
                    text={ticket.kind}
                    fg={ticket.kind === "bug" ? "#C0392B" : "#2A6AAC"}
                    bg={ticket.kind === "bug" ? "#FDECEA" : "#D2E4FC"}
                  />
                )}
                {ticket.severity && (
                  <Pill
                    text={ticket.severity.replace(/^\d-/, "")}
                    fg="#B87333" bg="#FCEFD9"
                  />
                )}
                {ticket.tier && <Pill text={ticket.tier} fg={T.ink2} bg={T.white} bd={T.line} />}
                {ticket.current_step && (
                  <Pill text={ticket.current_step} fg={T.plum} bg={T.plumSoft} />
                )}
              </div>

              {ticket.summary && (
                <div
                  style={{ fontSize: 17.5, lineHeight: 1.58, color: T.ink2, marginBottom: 14 }}
                  dangerouslySetInnerHTML={{ __html: inline(ticket.summary) }}
                />
              )}

              {/* Progress — the one number that says where this actually is. */}
              {ticket.iscs.length > 0 && (() => {
                const done = ticket.iscs.filter((i) => i.done).length;
                const pct = Math.round((done / ticket.iscs.length) * 100);
                return (
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{
                      flex: 1, height: 6, background: T.white,
                      border: `1px solid ${T.line}`, borderRadius: 999, overflow: "hidden",
                    }}>
                      <div style={{
                        width: `${pct}%`, height: "100%",
                        background: done === ticket.iscs.length ? T.green : T.sky,
                      }} />
                    </div>
                    <span style={{
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                      fontSize: 12.5, fontWeight: 700, color: T.ink2, whiteSpace: "nowrap",
                    }}>
                      {done}/{ticket.iscs.length} done
                    </span>
                  </div>
                );
              })()}
            </div>

            {/* ── LAYER 2 · THE DETAIL ───────────────────────────────── */}
            {ticket.iscs.length > 0 && (
              <div style={{ marginBottom: 22 }}>
                <Label text="Criteria" color={T.ink3} />
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {ticket.iscs.map((i) => (
                    <div key={i.id} style={{
                      display: "flex", gap: 10, alignItems: "flex-start",
                      fontSize: 16.5, lineHeight: 1.52,
                      color: i.done ? T.ink3 : T.ink2,
                    }}>
                      <span style={{
                        flexShrink: 0, marginTop: 2, width: 13, height: 13,
                        borderRadius: 4, border: `1.5px solid ${i.done ? T.green : T.line}`,
                        background: i.done ? T.green : "transparent",
                        color: T.white, fontSize: 10.5, fontWeight: 800,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        lineHeight: 1,
                      }}>{i.done ? "✓" : ""}</span>
                      <span style={{ textDecoration: i.done ? "line-through" : "none" }}>
                        {i.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {SECTIONS.map((sec) => {
              const value = ((ticket[sec.key] as string | null) ?? "").trim();
              if (!value) return null;
              const st = SECTION_STYLE[sec.key as string] ?? { form: "quiet" as SectionForm, label: sec.label };
              return <Section key={sec.key as string} form={st.form} label={st.label} body={value} />;
            })}

            {ticket.log && ticket.log.trim() && (
              <Section form="mono" label="Log" body={ticket.log} />
            )}

            <div style={{
              fontSize: 12.5, color: T.ink3, paddingTop: 12,
              letterSpacing: "0.05em", textTransform: "uppercase",
            }}>
              Updated {new Date(ticket.updated_at).toLocaleString()}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

(TicketDetailPage as unknown as { path: string }).path = "/tickets/:slug";
